import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip, Table, CopyButton, Callout } from '../../../components';

export const IndexingSection = ({ id, siteUrl, data }) => {
  const [urlResolveExpanded, setUrlResolveExpanded] = useState(false);
  const [robotsTxtExpanded, setRobotsTxtExpanded] = useState(false);
  const [xmlSitemapExpanded, setXmlSitemapExpanded] = useState(false);

  // Default criteria fallback
  const defaultCriteria = {
    status: 'error',
    iconType: 'error',
    isEmpty: true,
    message: '',
    importance: { level: 0, text: '' },
    solvability: { level: 0, text: '' },
  };

  // Extract transformed data with fallback
  const urlResolve = data?.urlResolve || defaultCriteria;
  const robotsTxt = data?.robotsTxt || defaultCriteria;
  const xmlSitemap = data?.xmlSitemap || defaultCriteria;
  const hreflangTags = data?.hreflangTags || defaultCriteria;
  const errorCount = data?.errorCount || 0;
  const toImproveCount = data?.toImproveCount || 0;

  // Build URL resolve table data
  const urlResolveTableData = urlResolve?.data?.urlTable?.length > 0
    ? [
        ['URL', 'Resolved URL', 'Status Code'],
        ...(urlResolve?.data?.urlTable?.map((row) => [
          row?.url || '',
          row?.resolvedUrl || '',
          row?.statusCode ?? '-',
        ]) || []),
      ]
    : [['URL', 'Resolved URL', 'Status Code']];

  // Build XML Sitemap table data
  const xmlSitemapTableData = xmlSitemap?.data?.sitemaps?.length > 0
    ? [
        ['URL'],
        ...(xmlSitemap?.data?.sitemaps?.map((sitemap) => [
          sitemap?.url || '',
        ]) || []),
      ]
    : [['URL']];

  // Build Hreflang Tags table data
  const hreflangTagsTableData = hreflangTags?.data?.tags?.length > 0
    ? [
        ['HREF', 'HREFLang', 'Correct?', 'Source'],
        ...(hreflangTags?.data?.tags?.map((tag) => [
          tag?.href || '',
          tag?.hreflang || '',
          tag?.isCorrect ? 'Yes' : 'No',
          tag?.source || '',
        ]) || []),
      ]
    : [['HREF', 'HREFLang', 'Correct?', 'Source']];

  return (
    <ResultsItem 
      id={id}
      title="Indexing" 
      tooltip="People find content online through search engines, which is why it's vital to get your website crawled and indexed by search engine crawlers, like from Google or Bing. With our SEO checker, you can learn your site's index status, plus if it follows best practices for robots.txt files and XML sitemaps."
      errorCount={errorCount}
      toImproveCount={toImproveCount}
    >
      <ResultSubItem
        title="URL Resolve"
        tooltip="A URL resolve ensures your website uses the proper redirects to send users and search engines to your preferred domain version, like 'http' vs. 'https'"
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={urlResolve?.iconType || 'error'} 
                showTooltip={false} 
              />
            }
          >
            {!urlResolve?.isEmpty && urlResolve?.data ? (
              <div className="table-content-wrapper">
                <Table
                  data={urlResolveTableData}
                  className="table-url-resolve"
                  id="url-resolve-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#url-resolve-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">{urlResolve?.message || 'No urls were identified.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={urlResolveExpanded}
            onClick={() => setUrlResolveExpanded(!urlResolveExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={urlResolveExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's URL Resolve Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Ensure content redirects to the correct version of your site, like example.com to www.example.com</li>
                <li>Use your .htaccess file to create redirects to your preferred domain</li>
                <li>Create redirects when moving to a new domain name, versus allowing the old site to expire</li>
              </ul>
            </IconContent>
          }
          sideContent={
            urlResolve?.importance?.level > 0 || urlResolve?.solvability?.level > 0 ? (
              <PriorityIndicatorGroup>
                {urlResolve?.importance?.level > 0 && (
                  <PriorityIndicator text={urlResolve?.importance?.text || ''} level={urlResolve?.importance?.level || 0} />
                )}
                {urlResolve?.solvability?.level > 0 && (
                  <PriorityIndicator text={urlResolve?.solvability?.text || ''} level={urlResolve?.solvability?.level || 0} />
                )}
              </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/internet/what-is-a-301-redirect/" target="_blank" rel="noopener noreferrer">
                    What Is a 301 Redirect?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/seo/learn/what-redirects-are-seo-friendly/" target="_blank" rel="noopener noreferrer">
                    How to Create SEO-Friendly Redirects
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/internet/my-website-is-not-secure/" target="_blank" rel="noopener noreferrer">
                    How to Secure & Redirect Your Site From HTTP to HTTPS
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Robots.txt"
        tooltip="Your robots.txt file is a file that provides search engine crawlers with instructions for crawling and indexing your site."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={robotsTxt?.iconType || 'error'} 
                showTooltip={false} 
              />
            }
          >
            {!robotsTxt?.isEmpty && robotsTxt?.data ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p className="text-primary">
                    Robots.txt is properly configured and accessible.
                  </p>
                  <p className="text-secondary">
                    {robotsTxt?.data?.url || ''}
                  </p>
                </div>
                <Callout>
                  <p>A robots.txt file allows you to restrict the access of search engine crawlers to prevent them from accessing specific pages or directories. They also point the web crawler to your page's XML sitemap file.</p>
                  <p>Your site currently has a robots.txt file. You can use Google Search Console's Robots.txt Tester to submit and test your robots.txt file and to make sure Googlebot isn't crawling any restricted files.</p>
                </Callout>
              </div>
            ) : (
              <p className="text-primary">{robotsTxt?.message || 'No robots.txt file was found.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={robotsTxtExpanded}
            onClick={() => setRobotsTxtExpanded(!robotsTxtExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={robotsTxtExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Robots.txt Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Test your robots.txt file with the Google Search Console robots.txt tool</li>
                <li>Prevent search engines from crawling duplicate content, like PDFs, with your robots.txt file</li>
                <li>Use your robots.txt file to direct crawlers to your XML sitemap</li>
              </ul>
            </IconContent>
          }
          sideContent={
            robotsTxt?.importance?.level > 0 || robotsTxt?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {robotsTxt?.importance?.level > 0 && (
                  <PriorityIndicator text={robotsTxt?.importance?.text || ''} level={robotsTxt?.importance?.level || 0} />
                )}
                {robotsTxt?.solvability?.level > 0 && (
                  <PriorityIndicator text={robotsTxt?.solvability?.text || ''} level={robotsTxt?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/web-design/robotstxt/" target="_blank" rel="noopener noreferrer">
                    Robots.txt | How to Use It and How It Affects SEO
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/seo/what-is-crawlability-indexability/" target="_blank" rel="noopener noreferrer">
                    What Is Crawlability and Indexability?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/search-engine-optimization/get-new-site-pages-indexed-quickly-possible/" target="_blank" rel="noopener noreferrer">
                    How to Get Your Newest Pages Indexed Fast
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="XML Sitemap"
        tooltip="Your XML sitemap is a file that provides search engines with a roadmap to your website."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={xmlSitemap?.iconType || 'error'} 
                showTooltip={false} 
              />
            }
          >
            {!xmlSitemap?.isEmpty && xmlSitemap?.data ? (
              <div className="table-content-wrapper">
                <Table
                  data={xmlSitemapTableData}
                  className="table-xml-sitemap"
                  id="xml-sitemap-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#xml-sitemap-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">{xmlSitemap?.message || 'No sitemap found in your robots.txt.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={xmlSitemapExpanded}
            onClick={() => setXmlSitemapExpanded(!xmlSitemapExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={xmlSitemapExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's XML Sitemap Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Use a preferred URL format, like with "www," for your XML sitemap</li>
                <li>Include the correct protocol, like "http" or "https" in your XML sitemap</li>
                <li>Add a link to your XML sitemap in your website's footer</li>
              </ul>
            </IconContent>
          }
          sideContent={
            xmlSitemap?.importance?.level > 0 || xmlSitemap?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {xmlSitemap?.importance?.level > 0 && (
                  <PriorityIndicator text={xmlSitemap?.importance?.text || ''} level={xmlSitemap?.importance?.level || 0} />
                )}
                {xmlSitemap?.solvability?.level > 0 && (
                  <PriorityIndicator text={xmlSitemap?.solvability?.text || ''} level={xmlSitemap?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/seo/what-is-an-xml-sitemap/" target="_blank" rel="noopener noreferrer">
                    What Is an XML Sitemap?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="https://www.youtube.com/watch?v=3W80XGl0Xok" target="_blank" rel="noopener noreferrer">
                    [Video] Everything You Need to Know About XML Sitemaps
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Hreflang Tags"
        tooltip="Hreflang tags specifies a language and optional geographical restrictions on a web page."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={hreflangTags?.iconType || 'error'} 
                showTooltip={false} 
              />
            }
          >
            {!hreflangTags?.isEmpty && hreflangTags?.data ? (
              <div className="table-content-wrapper">
                <Table
                  data={hreflangTagsTableData}
                  className="table-hreflang-tags"
                  id="hreflang-tags-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#hreflang-tags-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">
                {hreflangTags?.message || 'No Hreflang Tags were identified.'}
              </p>
            )}
          </IconContent>
        }
      />
    </ResultsItem>
  );
};
